import React, { useMemo, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import SearchableMultiSelectListCst from "@components/SearchableMultiSelectListCst";
import { useSkillsList } from "../hooks/useSkillsList";
import { SkillListItem } from "../types";
import SimpleNamedRow from "./SimpleNamedRow";

const PAGE_SIZE = 10;

interface SkillsSectionProps {
  initiativeId: number;
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}

const SkillsSection: React.FC<SkillsSectionProps> = ({
  initiativeId,
  selectedIds,
  onChange,
}) => {
  const { t } = useTranslation("wizard-activity");
  const theme = useTheme();
  const { id: corporateId = "" } = useParams();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [search, setSearch] = useState("");

  const { items, totalItems, totalPages, isLoading } = useSkillsList({
    corporateId,
    initiativeId,
    page,
    pageSize,
    search: search || undefined,
  });

  const selectedSet = useMemo(
    () => new Set<string | number>(selectedIds),
    [selectedIds],
  );

  const handleToggle = (id: string | number, next: boolean) => {
    const numericId = typeof id === "string" ? Number(id) : id;
    const set = new Set(selectedIds);
    if (next) set.add(numericId);
    else set.delete(numericId);
    onChange(Array.from(set));
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box>
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 700,
            color: theme.palette.text.primary,
          }}
        >
          {t("editorial.skills.title")}
        </Typography>
        <Typography
          sx={{
            fontSize: "13px",
            color: theme.palette.text.primary,
            mt: 0.5,
          }}
        >
          {t("editorial.skills.description")}
        </Typography>
      </Box>

      <SearchableMultiSelectListCst<SkillListItem>
        items={items}
        getId={(s) => s.id}
        renderRow={({ item }) => <SimpleNamedRow item={item} />}
        selectedIds={selectedSet}
        onToggle={handleToggle}
        initialSearchValue={search}
        onSearchSubmit={handleSearch}
        searchPlaceholder={t("editorial.search-placeholder") || ""}
        searchButtonLabel={t("editorial.search-button") || ""}
        page={page}
        pageSize={pageSize}
        totalItems={totalItems}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={(next) => {
          setPageSize(next);
          setPage(1);
        }}
        isLoading={isLoading}
        selectedCountLabel={
          t("editorial.selected-count", { count: selectedIds.length }) || ""
        }
        emptyLabel={t("editorial.skills.empty") || ""}
      />
    </Box>
  );
};

export default SkillsSection;
